import React, { type FC, forwardRef, type MouseEvent, type Ref, useCallback, useRef } from 'react';

import { Hint } from '@shared/ui/hint';

import { LEFT_POSITION_STYLE, RIGHT_POSITION_STYLE } from './config';
import type { ButtonPropsTypes } from './types';

import styles from './styles.module.scss';

const ButtonBase: FC<ButtonPropsTypes> = ({
	attachmentsLength,
	buttonSize,
	buttonType,
	children,
	className,
	disabled,
	hint,
	innerRef,
	isVisible,
	isLoading,
	onClick,
	svg,
	svgPosition = 'left',
	...props
}) => {
	let btnClass: string;
	switch (buttonType) {
		case 'primary': {
			btnClass = styles.Primary;
			break;
		}
		case 'secondary': {
			btnClass = styles.Secondary;
			break;
		}
		case 'destructive': {
			btnClass = styles.Destructive;
			break;
		}
		case 'approve': {
			btnClass = styles.Approve;
			break;
		}
		case 'icon': {
			btnClass = styles.Icon;
			break;
		}
		case 'icon-primary': {
			btnClass = styles.IconPrimary;
			break;
		}
		case 'icon-mini': {
			btnClass = styles.IconMini;
			break;
		}
		case 'icon-micro': {
			btnClass = styles.IconMicro;
			break;
		}
		case 'icon-border': {
			btnClass = styles.IconBorder;
			break;
		}
		case 'attach': {
			btnClass = styles.Attach;
			break;
		}
		case 'expand': {
			btnClass = styles.Expand;
			break;
		}
		case 'expand-white': {
			btnClass = styles.ExpandWhite;
			break;
		}
		default: {
			btnClass = styles.Default;
		}
	}
	if (buttonSize === 'sm') {
		btnClass += ` ${styles.SizeSmall}`;
	}
	if (className) {
		btnClass += ` ${className}`;
	}

	const attachmentsCounter =
		buttonType === 'attach' && attachmentsLength && attachmentsLength > 0 ? (
			<span className={styles.AttachmentsCount}>{attachmentsLength}</span>
		) : null;

	const isLeftPosition = svgPosition === LEFT_POSITION_STYLE;
	const isRightPosition = svgPosition === RIGHT_POSITION_STYLE;
	const svgPositionClass = isLeftPosition ? LEFT_POSITION_STYLE : RIGHT_POSITION_STYLE;
	const getIconClass = () => {
		// если иконка находится внутри кнопки с текстом - навешиваем на неё класс для стилизации
		return !buttonType ||
			!['icon', 'icon-primary', 'icon-border', 'attach', 'icon-mini', 'icon-micro'].includes(
				buttonType
			)
			? ` ${styles.TextIcon} ${styles[svgPositionClass]}`
			: '';
	};

	const svgString = typeof svg === 'string' ? svg : '';
	const svgIcon = svg ? (
		<span className={getIconClass()} dangerouslySetInnerHTML={{ __html: svgString }} />
	) : null;

	// собственный стабильный ref для хинта, плюс прокидываем внешний ref, если он есть
	const ownRef = useRef<HTMLButtonElement | null>(null);
	const setButtonRef = useCallback(
		(node: HTMLButtonElement | null) => {
			ownRef.current = node;
			if (!innerRef) return;
			if (typeof innerRef === 'function') innerRef(node);
			else (innerRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
		},
		[innerRef]
	);

	const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
		if (disabled) {
			e.preventDefault();

			return;
		}
		if (onClick) {
			onClick(e);
		}
	};
	if (isVisible !== undefined && !isVisible) {
		return null;
	}
	if (disabled) props.tabIndex = -1; // отключаем фокус по нажатию на tab

	return (
		<>
			<button
				className={btnClass}
				type="button"
				{...props}
				ref={setButtonRef}
				aria-label={typeof hint === 'string' ? hint : undefined}
				data-disabled={disabled ? 'true' : 'false'} // используем data-disabled вместо disabled для того чтобы eventListener работал и мы могли отображать hint
				data-loader={isLoading ? 'true' : 'false'}
				onClick={handleClick}
			>
				{isLeftPosition && svgIcon}
				{children}
				{attachmentsCounter}
				{isRightPosition && svgIcon}
			</button>
			{hint && <Hint refParent={ownRef}>{hint}</Hint>}
		</>
	);
};

export const Button = forwardRef<HTMLButtonElement, ButtonPropsTypes>(
	(props, ref: Ref<HTMLButtonElement> | null) => <ButtonBase innerRef={ref} {...props} />
);
